import { useState } from "react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";

// Schedule an interview from the candidate detail page (Phase F, pt.26).
// Compact — date + time + duration + attendees + optional link/notes.

export default function ScheduleInterviewButton({ candidate, onScheduled }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const now = new Date();
  const defDate = now.toISOString().slice(0, 10);
  const defTime = "10:00";
  const [form, setForm] = useState({
    date: defDate,
    time: defTime,
    duration_minutes: 45,
    location_or_link: "",
    attendees: [
      candidate?.contact?.email,
      "bpayne@mrelectricmo.com",
      "ntoms@mrelectricmo.com",
    ].filter(Boolean).join(", "),
    notes: "",
  });

  async function submit() {
    setSaving(true);
    try {
      // Compose local scheduled_at as ISO (naive local time, server treats as CT).
      const scheduled_at = new Date(`${form.date}T${form.time}:00`).toISOString();
      const body = {
        candidate_id: candidate.id,
        scheduled_at,
        duration_minutes: Number(form.duration_minutes),
        attendee_emails: form.attendees.split(",").map((s) => s.trim()).filter(Boolean),
        location_or_link: form.location_or_link,
        notes: form.notes,
      };
      const { data } = await api.post("/hiring/calendar/interviews", body);
      const msg = data.meet_link
        ? "Interview scheduled — Google Meet link attached"
        : `Interview scheduled — event ${data.google_event_id}`;
      toast.success(msg);
      setOpen(false);
      if (onScheduled) onScheduled(data);
    } catch (e) {
      toast.error(extractApiError(e) || "Schedule failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        variant="outline"
        data-testid="hiring-schedule-interview-btn"
      >
        <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Schedule interview
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hiring-schedule-interview-modal">
          <DialogHeader>
            <DialogTitle>Schedule interview — {candidate?.contact?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#4b5563] mb-1 block">Date</label>
                <Input type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="hiring-schedule-date" />
              </div>
              <div>
                <label className="text-xs text-[#4b5563] mb-1 block">Time (CT)</label>
                <Input type="time" value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  data-testid="hiring-schedule-time" />
              </div>
              <div>
                <label className="text-xs text-[#4b5563] mb-1 block">Duration (min)</label>
                <Input type="number" min="15" max="240" step="15"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  data-testid="hiring-schedule-duration" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#4b5563] mb-1 block">Attendees (comma-separated emails)</label>
              <Input value={form.attendees}
                onChange={(e) => setForm({ ...form, attendees: e.target.value })}
                data-testid="hiring-schedule-attendees" />
            </div>
            <div>
              <label className="text-xs text-[#4b5563] mb-1 block">Location or meeting link (optional)</label>
              <Input value={form.location_or_link}
                onChange={(e) => setForm({ ...form, location_or_link: e.target.value })}
                placeholder="Office address, or leave blank"
                data-testid="hiring-schedule-location" />
              <div className="text-[11px] text-[#4b5563] mt-1">
                A Google Meet link is attached to every interview automatically — use this field for an in-person address or an external meeting link.
              </div>
            </div>
            <div>
              <label className="text-xs text-[#4b5563] mb-1 block">Notes (optional)</label>
              <Textarea rows={3} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="hiring-schedule-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="hiring-schedule-submit">
              {saving ? "Scheduling…" : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
